import React, { useContext, useEffect, useLayoutEffect, useState } from 'react';
import { Carousel, Card, Typography, Spin } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import CAgregarCarrito from './CAgregarCarrito';
import { SucursalContext } from '../../../../context/sucursalContext';
import productoOfertaImg from '../../../../assets/producto_oferta.png';

const { Title, Text } = Typography;

const slickArrowBase = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 40,
  height: 40,
  zIndex: 2,
  border: 'none',
  borderRadius: 4,
  cursor: 'pointer',
};

const PrevArrow = (props) => {
  const { className, style, onClick } = props;
  return (
    <button
      type="button"
      className={className}
      style={{
        ...style,
        ...slickArrowBase,
        background: 'rgba(0,0,0,0.35)',
        color: '#fff',
      }}
      onClick={onClick}
      aria-label="Anterior"
    >
      <LeftOutlined />
    </button>
  );
};

const NextArrow = (props) => {
  const { className, style, onClick } = props;
  return (
    <button
      type="button"
      className={className}
      style={{
        ...style,
        ...slickArrowBase,
        background: '#e10a25',
        color: '#fff',
      }}
      onClick={onClick}
      aria-label="Siguiente"
    >
      <RightOutlined />
    </button>
  );
};

const CCarruselOfertasInicio = () => {
  const { sucursal } = useContext(SucursalContext);
  const [articulos, setArticulos] = useState([]);
  // Importante: empezar en true para no renderizar "vacío" antes del primer fetch (useEffect corre después del primer paint).
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Si la sucursal aparece después del primer render, evitar un frame con loading=false y lista vacía (se ocultaba todo el carrusel).
  useLayoutEffect(() => {
    if (sucursal?.suc_id) {
      setLoading(true);
    }
  }, [sucursal?.suc_id]);

  useEffect(() => {
    if (!sucursal?.suc_id) {
      setArticulos([]);
      setLoading(false);
      return;
    }

    const load = async () => {
      setLoading(true);
      try {
        const queryParams = new URLSearchParams({
          page: 1,
          limit: 24,
          asu_sucursal: String(sucursal.suc_id),
          asu_habilitado: 'true',
          solo_ofertas_vigentes: 'true',
          ordenar_por: 'nombre_asc',
        });
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/articulo-sucursal/sucursal/?${queryParams.toString()}`
        );
        const data = await res.json();
        if (!res.ok) {
          console.error('Ofertas: respuesta HTTP', res.status, data);
          setArticulos([]);
          return;
        }
        setArticulos(data.data ?? []);
      } catch (e) {
        console.error('Error al cargar ofertas:', e);
        setArticulos([]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [sucursal?.suc_id]);

  if (!sucursal?.suc_id) {
    return null;
  }

  if (loading) {
    return (
      <div style={{ padding: '1rem 0', textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }

  if (articulos.length === 0) {
    return null;
  }

  const handleVerDetalle = (item) => {
    navigate('/detalle-articulo-inicio', {
      state: {
        articulo: item,
        returnTo: { filters: { asu_habilitado: true, asu_sucursal: sucursal.suc_id, ordenar_por: 'relevancia' }, paginationCurrent: 1 },
      },
    });
  };

  return (
    <div
      className="carrusel-ofertas-inicio"
      style={{ marginBottom: 20, marginTop: 8, paddingLeft: 4, paddingRight: 4 }}
    >
      {/* Ant Design dibuja otra flecha con ::after en .slick-prev/.slick-next; ocultamosla para no duplicar con los iconos */}
      <style>
        {`
          .carrusel-ofertas-inicio .slick-prev::after,
          .carrusel-ofertas-inicio .slick-next::after {
            display: none !important;
          }
        `}
      </style>
      <Title
        level={4}
        style={{
          marginBottom: 12,
          paddingLeft: 4,
          fontWeight: 700,
          color: '#e10a25',
          letterSpacing: '0.03em'
        }}
      >
        Ofertas
      </Title>
      <Carousel
        arrows
        dots={false}
        slidesToShow={Math.min(4, articulos.length)}
        slidesToScroll={1}
        infinite={articulos.length > 4}
        prevArrow={<PrevArrow />}
        nextArrow={<NextArrow />}
        responsive={[
          {
            breakpoint: 1200,
            settings: {
              slidesToShow: Math.min(3, articulos.length),
              slidesToScroll: 1,
              infinite: articulos.length > 3,
            },
          },
          {
            breakpoint: 992,
            settings: {
              slidesToShow: Math.min(2, articulos.length),
              slidesToScroll: 1,
              infinite: articulos.length > 2,
            },
          },
          {
            breakpoint: 576,
            settings: {
              slidesToShow: 1,
              slidesToScroll: 1,
              infinite: articulos.length > 1,
            },
          },
        ]}
      >
        {articulos.map((item, index) => {
          const aem = item.articulo_empresa;
          const asu = item.datos_sucursal;
          const porcDesc =
            asu?.asu_porc_descuento != null ? Number(asu.asu_porc_descuento) : null;
          const mostrarTachadoYBadge = porcDesc != null && porcDesc > 0;
          return (
            <div key={aem?.aem_id ?? index} style={{ padding: '0 8px' }}>
              <Card
                hoverable
                onClick={() => handleVerDetalle(item)}
                styles={{ body: { padding: '12px 10px' } }}
                style={{
                  borderRadius: 8,
                  border: '1px solid #f0f0f0',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
                cover={
                  <div style={{ position: 'relative', backgroundColor: '#fff', padding: '8px 0' }}>
                    <img
                      src={productoOfertaImg}
                      alt=""
                      role="presentation"
                      style={{
                        position: 'absolute',
                        top: 4,
                        left: 4,
                        zIndex: 2,
                        width: 'auto',
                        maxWidth: '42%',
                        maxHeight: 44,
                        height: 'auto',
                        objectFit: 'contain',
                        pointerEvents: 'none',
                      }}
                    />
                    <img
                      alt={aem.aem_nombre}
                      src={aem.aem_imagen || 'https://via.placeholder.com/300x200.png?text=Sin+Imagen'}
                      style={{
                        height: 160,
                        width: '100%',
                        objectFit: 'contain',
                        display: 'block',
                      }}
                    />
                  </div>
                }
              >
                <Title
                  level={5}
                  style={{
                    marginBottom: 6,
                    marginTop: 0,
                    fontSize: 14,
                    lineHeight: 1.35,
                    minHeight: 38,
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {aem.aem_nombre}
                </Title>
                <div style={{ minHeight: 88 }}>
                  {mostrarTachadoYBadge && (
                    <>
                      <Text delete style={{ fontSize: 12, color: '#999', display: 'block' }}>
                        $
                        {asu?.asu_precioventa != null
                          ? Number(asu.asu_precioventa).toLocaleString('es-AR')
                          : ''}
                      </Text>
                      <span
                        style={{
                          display: 'inline-block',
                          background: '#ffeb3b',
                          color: '#000',
                          fontSize: 11,
                          fontWeight: 700,
                          padding: '2px 8px',
                          borderRadius: 4,
                          marginTop: 4,
                          marginBottom: 4,
                        }}
                      >
                        {porcDesc}%
                      </span>
                    </>
                  )}
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: 18,
                      color: '#e10a25',
                      marginTop: 4,
                    }}
                  >
                    $
                    {asu?.asu_precio_con_descuento != null
                      ? Number(asu.asu_precio_con_descuento).toLocaleString('es-AR')
                      : ''}
                  </div>
                  {asu?.asu_precio_siniva != null && (
                    <div
                      style={{
                        fontSize: '0.75rem',
                        color: '#888',
                        marginTop: 4,
                        lineHeight: 1.3,
                      }}
                    >
                      Precio sin impuestos nacionales: $
                      {Number(asu.asu_precio_siniva).toLocaleString('es-AR')}
                    </div>
                  )}
                </div>
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    marginTop: 'auto',
                    paddingTop: 8,
                    display: 'flex',
                    justifyContent: 'center',
                    width: '100%',
                  }}
                >
                  <CAgregarCarrito articulo={item} esVistaInicioGrid />
                </div>
              </Card>
            </div>
          );
        })}
      </Carousel>
    </div>
  );
};

export default CCarruselOfertasInicio;
